import React, { useEffect, useMemo, useState } from "react";
import { ClientApiProps, FilterPicker, queryFromFilters, queryToFilters } from "../../common";
import { Acma_senderidauthorisation_statuscode, Client, Hint, IAuthorisation, IFilter, ISettings, getApiClient } from "../../api";
import { Button, DataGrid, DataGridColumn, Header } from "acma-spa";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation, useNavigate } from "react-router-dom";
import { notEmpty } from "../../stores/ListCache";
import { RegisterStore } from "../../stores/RegisterStore";
import { RequestModal } from "./RequestModal";
import { ValidateTokenModal } from "./ValidateTokenModal";
import { ContentModal } from "../ContentModal";

export interface RegisterPageProps extends ClientApiProps {
  settings: ISettings[]
  onSettings: (v: ISettings[]) => any
}

export const RegisterPage: React.FC<RegisterPageProps> = (props) => {
  var location = useLocation();

  const [rows, setRows] = useState<IAuthorisation[] | null>(null);
  const [filters, setFilters] = useState<IFilter[]>(() => {
    return queryToFilters(location.search);
  });
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [sorting, setSorting] = useState({ columnIndex: 0, descending: false });
  const [selection, setSelection] = useState<IAuthorisation[]>([]);
  const [requesting, setRequesting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const pageSize = 50;

  var navigate = useNavigate();

  const load = async (client: Client) => {
    if (pageNumber == 1) {
      setRows(null);
      console.log("Loading...")
    }

    try {
      const sortColumnList = [Hint.Name, Hint.Company, Hint.Contact, Hint.CreatedOn, Hint.Status];
      var sortColumn = Hint.Name;
      var sortDesc = false;
      if (sorting.columnIndex >= 0 && sorting.columnIndex < sortColumnList.length) {
        sortColumn = sortColumnList[sorting.columnIndex];
        sortDesc = sorting.descending;
      }

      var store = new RegisterStore(client);

      var newRows = await store.getRegisteredAlphaTags(
        filters.filter(r => r.hint == Hint.Abn).map(r => r.value).filter(notEmpty),
        filters.filter(r => r.hint == Hint.Name).map(r => r.value).filter(notEmpty),
        filters.filter(r => r.hint == Hint.Contact).map(r => r.value).filter(notEmpty),
        filters.filter(r => r.hint == Hint.Status).map(r => r.value as Acma_senderidauthorisation_statuscode),
        filters.filter(r => !r.hint).map(r => r.value)[0],
        sortColumn,
        sortDesc,
        pageNumber,
        pageSize);

      if (newRows.length < pageSize)
        setHasMore(false);

      pageNumber == 1 ? setRows(newRows) : setRows(rows?.concat(newRows) ?? newRows);
    }
    catch (e) {
      if (!rows)
        setRows([]);
    }
  }

  const reload = async () => {
    setSelection([]);

    if (pageNumber == 1) {
      const [client, abort] = getApiClient(
        props.baseUrl,
        props.b2cDomainName,
        props.msalClient
      );
      await load(client);
    }
    else {
      setPageNumber(1);
    }
  }

  useEffect(() => {
    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );

    load(client);

    return function () {
      abort.abort();
    }

  }, [filters, pageNumber, sorting])

  const columns: DataGridColumn<IAuthorisation>[] = [
    {
      label: "Alpha tag",
      value: r => <div onClick={e => setSelection([r])}>{r.name}</div>
    },
    {
      label: "Registrant",
      value: r => <div onClick={e => setSelection([r])}>{r.clientName}</div>
    },
    {
      label: "Contact",
      value: r => <div onClick={e => setSelection([r])}><a href={ `mailto:${r.contactEmail}`}>{r.contactName}</a></div>
    },
    {
      label: "Created on",
      value: r => <div onClick={e => setSelection([r])}>{r.createdOn?.toLocaleDateString()}</div>
    },
    {
      label: "Status",
      value: r => <div onClick={e => setSelection([r])}>{r.status ?? "New"}</div>
    }
  ]

  const onSort = (columnIndex: number, descending: boolean) => {
    if (columnIndex < 0) {
      columnIndex = sorting.columnIndex;
    }
    setSorting({ columnIndex, descending });
    setPageNumber(1);
  }

  const onFilter = (v: IFilter[]) => {
    setFilters(v);
    setPageNumber(1);
    navigate("?" + queryFromFilters(v));
  }

  const getFilter = async (s: string) => {
    if (!s)
      return [];

    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );

    return await client.getRegisteredAlphaTagFilters(s);
  }

  const onPaginate = () => {
    if (rows && rows.length > 0) {
      setPageNumber(pageNumber + 1)
    }
  }

  const onCancel = async () => {
    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );

    const store = new RegisterStore(client);
    await store.cancelAuthorisations(selection.map(r => r.authorisationId).filter(notEmpty));
    reload();
  }

  const itemsToCancel = selection.filter(r => r.status == Acma_senderidauthorisation_statuscode.Requested)
  const itemsToRequest = selection.filter(r => r.status ? false : true)
  const defaultOptions = useMemo(() => Object.keys(Acma_senderidauthorisation_statuscode)
    .filter(r=> r != "Revoked" && r != "Issued")
    .map(r => ({ label: r, value: r, hint: Hint.Status })), [])

  return <>
    <Header label="Validate a token you have received, or select a new alpha tag to request confirmation" />
    <div className="row">
      <div className="col-lg-7 mb-2 mt-1">
        <Button id="validateToken" label="Validate a token" onClick={() => setValidating(true)} className="mr-2 mt-2" />
        {itemsToRequest.length > 0 && itemsToRequest.length <= 10 ?
          <Button id="requestConfirmation" label="Request confirmation" onClick={() => setRequesting(true)} mode="secondary" className="mr-2 mt-2" /> : <></>}
        {itemsToCancel.length > 0 && itemsToCancel.length <= 10 ?
          <Button id="cancel" label="Cancel request" onClick={() => setCancelling(true)} mode="secondary" className="mt-2" />: <></>}
      </div>
      <div className="col-lg-5 mb-2 mt-1">
        <FilterPicker id="picker" value={filters} onChange={onFilter} getOptions={getFilter} defaultOptions={defaultOptions} />
      </div>
    </div>

    <InfiniteScroll
      dataLength={rows?.length ?? 0} //This is important field to render the next data
      next={onPaginate}
      hasMore={hasMore}
      loader={rows == null ? <></> : <h4>Loading...</h4>}>
      <DataGrid<IAuthorisation>
        columns={columns}
        rows={rows ?? []}
        noRowsMessage={rows == null ? "Loading..." : "No confirmations found"}
        sort={{
          columnIndex: sorting.columnIndex,
          descending: sorting.descending,
          onChange: onSort
        }}
        select={{
          rows: selection,
          onChange: setSelection
        }}
      />
    </InfiniteScroll>

    {requesting ? <RequestModal {...props} open={requesting} onClose={() => setRequesting(false)} onChange={reload} selection={itemsToRequest} settings={props.settings} onSettings={ props.onSettings} /> : <></>}
    {validating ? <ValidateTokenModal {...props} open={validating} onClose={() => setValidating(false)} onChange={reload} /> : <></>}

    <ContentModal open={cancelling} onClose={() => setCancelling(false)} onConfirm={onCancel} title="Cancelling request" spinnerText="Cancelling...">
      <p>Do you want to cancel request to use <b>{selection.map(r => r.name).join(", ")}</b> alpha tags?</p>
    </ContentModal>
  </>
}